import { Skeleton, VStack } from '@chakra-ui/react';
import { lastDayOfMonth, lastDayOfWeek, startOfWeek } from 'date-fns';
import React from 'react';
import { useQueryClient } from 'react-query';
import { Navigate } from 'react-router-dom';

import { AppError } from '../../../../framework/models/errors';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import { pageNames, pagePaths } from '../../../constants/Page';
import { useGlobalStateMachine } from '../../../GlobalStore';
import { getStateFromDate, useDateChangers } from '../../../hooks/DateChangers';
import {
  ASSIGNED_MATTER_CONTEXT_NAMES,
  usePostAssignedMatterSearch,
} from '../../../hooks/queries/assignedMatter';
import { usePostDeadlineSearch } from '../../../hooks/queries/deadline';
import useRadioCardGroup from '../../../hooks/RadioCard';
import useToastHook from '../../../hooks/ToastHook';
import {
  AssignedMatterSearchResult,
  DeadlineMode,
  UserSummary,
  WorkApply,
} from '../../../models/gen/models';
import DeadlineUtils from '../../../utils/DeadlineUtils';
import { Formatter } from '../../../utils/FormatUtils';
import Wireframe from '../../wireframe/Wireframe';
import DailyMatterList from './daily-matter-list/DailyMatterList';

export type DailyOperationState = AssignedMatterSearchResult & {
  outsourcerMemberSummary?: Array<UserSummary>;
  workApply?: WorkApply;
  leftDeadend?: string;
  rightDeadend?: string;
};

/**
 * 日次業務
 * @returns
 */
const DailyOperation: React.FunctionComponent = () => {
  const { state } = useGlobalStateMachine();
  if (TypeUtils.isNil(state.userInfo)) {
    return <Navigate to={pagePaths.ROOT} />;
  }

  const queryClient = useQueryClient();
  const setData = React.useCallback(
    (result: AssignedMatterSearchResult) =>
      queryClient.setQueryData(
        ASSIGNED_MATTER_CONTEXT_NAMES.POST_ASSIGNED_MATTER_SEARCH,
        result
      ),
    [queryClient]
  );
  const data = queryClient.getQueryData<AssignedMatterSearchResult | undefined>(
    ASSIGNED_MATTER_CONTEXT_NAMES.POST_ASSIGNED_MATTER_SEARCH
  );
  const postAssignedMatterSearch = usePostAssignedMatterSearch();
  const { toast } = useToastHook();

  const leftDeadend = React.useMemo(() => {
    if (!TypeUtils.isNilString(state.appConfig.facilitySettings?.PAST_LIMIT)) {
      const date = new Date();
      date.setMonth(
        date.getMonth() +
          (state.appConfig.facilitySettings!.PAST_LIMIT as number)
      );
      return getStateFromDate({}, date);
    }
    if (!TypeUtils.isNilString(state.appConfig.facilitySettings?.START_DATE)) {
      return getStateFromDate(
        {},
        Formatter.createDate(
          state.appConfig.facilitySettings!.START_DATE as string
        )
      );
    }
    return undefined;
  }, [state.appConfig.facilitySettings]);
  const rightDeadend = React.useMemo(() => {
    if (
      !TypeUtils.isNilString(state.appConfig.facilitySettings?.FUTURE_LIMIT)
    ) {
      const date = new Date();
      date.setMonth(
        date.getMonth() +
          (state.appConfig.facilitySettings!.FUTURE_LIMIT as number)
      );
      return getStateFromDate({}, date);
    }
    return undefined;
  }, [state.appConfig.facilitySettings]);

  const [displayRange, RadioCardGroup] = useRadioCardGroup<
    'day' | 'week' | 'month'
  >({
    optionValues: [
      { value: 'day', displayedValue: '3日' },
      { value: 'week', displayedValue: '1週間' },
      { value: 'month', displayedValue: '1ヶ月' },
    ],
    defaultValue: state.pageInfo.DAILY_OPERATION?.selected || 'day',
    key: 'DAILY_OPERATION',
  });
  const {
    DayChanger,
    MonthChanger,
    WeekChanger,
    state: dateState,
  } = useDateChangers({
    initialState:
      state.pageInfo.DAILY_OPERATION?.dateState || getStateFromDate(), // debug
    leftDeadend,
    rightDeadend,
    onChangeMonth: (dateStateInner) =>
      postAssignedMatterSearch.mutate(
        {
          year: dateStateInner.year,
          month: dateStateInner.month,
        },
        {
          onSuccess: (result) => {
            if (!TypeUtils.isNil(result)) {
              setData(result);
            }
          },
          onError: (error) =>
            toast(
              error instanceof AppError ? error : '勤怠状況の取得に失敗しました'
            ),
        }
      ),
  });
  const { data: deadlines, refetch } = usePostDeadlineSearch({
    modes: [DeadlineMode.Daily],
  });

  React.useState(() => {
    refetch();
    postAssignedMatterSearch.mutate(
      {
        year: dateState.year,
        month: dateState.month,
      },
      {
        onSuccess: (result) => {
          if (!TypeUtils.isNil(result)) {
            setData(result);
          }
        },
        onError: (error) =>
          toast(
            error instanceof AppError ? error : '日次業務の取得に失敗しました'
          ),
      }
    );
  });

  return (
    <Wireframe
      title={pageNames.DAILY_OPERATION}
      topMessages={deadlines?.results?.filter(DeadlineUtils.isDaily)}
    >
      <VStack width="100%">
        <RadioCardGroup label="表示形式" />
        {displayRange === 'day' ? (
          <DayChanger />
        ) : displayRange === 'week' ? (
          <WeekChanger />
        ) : (
          <MonthChanger />
        )}
        <Skeleton isLoaded={!postAssignedMatterSearch.isLoading} width="100%">
          <DailyMatterList
            {...(data || {})}
            dateState={dateState}
            startDate={
              displayRange === 'day'
                ? new Date(
                    dateState.year,
                    dateState.month - 1,
                    dateState.day - 1
                  )
                : displayRange === 'week'
                ? startOfWeek(
                    new Date(dateState.year, dateState.month - 1, dateState.day)
                  )
                : new Date(dateState.year, dateState.month - 1, 1)
            }
            endDate={
              displayRange === 'day'
                ? new Date(
                    dateState.year,
                    dateState.month - 1,
                    dateState.day + 1
                  )
                : displayRange === 'week'
                ? lastDayOfWeek(
                    new Date(dateState.year, dateState.month - 1, dateState.day)
                  )
                : lastDayOfMonth(
                    new Date(dateState.year, dateState.month - 1, 1)
                  )
            }
            outsourcerMemberSummary={data?.reference?.OutsourcerMemberSummaries}
          />
        </Skeleton>
      </VStack>
    </Wireframe>
  );
};

export default DailyOperation;
